import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "@/frontend/auth-client";
import { orpc } from "@/frontend/orpc-client";
import { useActiveMemberQuery } from "@/frontend/queries/dashboard/settings";
import { OnboardingPageLayout } from "./OnboardingPageLayout";
import { PersonaWorkspaceForm } from "./PersonaWorkspaceForm";
import { Button } from "@/frontend/components/ui/button";

/** Renders a retryable boundary when persona prerequisites cannot be loaded. */
function PersonaGateError({ retry }: { retry: () => void }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-body">
      <p role="alert">Could not load workspace setup.</p>
      <Button type="button" variant="outline" onClick={retry}>Try again</Button>
    </div>
  );
}

/**
 * One-time prompt for workspaces created before personas existed, where the
 * owner of an untyped workspace answers whose records these are on their next
 * login. Everyone else falls straight through. Ownership comes only from the
 * active workspace member row, matching the server-side authorization check.
 */
export function PersonaGate({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const isInviteAcceptPage = window.location.pathname === "/invite/accept";
  // `/delete-your-data` is a public patient page with its own portal sign-in.
  // A signed-in org member landing on it must reach the page, not a workspace prompt.
  const isDeletePatientDataPage = window.location.pathname === "/delete-your-data";

  const actorContextQuery = useQuery({
    ...orpc.org.getActorContext.queryOptions({ input: {} }),
    enabled: !!session,
  });
  const isOrgMember = actorContextQuery.data?.actorType === "orgMember";

  // Same query key OrganizationGate already populated, so this costs no request.
  const activeOrgQuery = useQuery({
    ...orpc.org.getFullOrganization.queryOptions({ input: {} }),
    enabled: !!session && isOrgMember,
  });

  const activeMemberQuery = useActiveMemberQuery(!!session && isOrgMember);
  const roles = (activeMemberQuery.data?.role ?? "")
    .split(",")
    .map((role) => role.trim())
    .filter(Boolean);
  const isOwner = roles.includes("owner");

  const activeOrg = activeOrgQuery.data;
  const untypedWorkspace =
    !!session &&
    !isInviteAcceptPage &&
    !isDeletePatientDataPage &&
    isOrgMember &&
    activeOrg &&
    activeOrg.accountType === null
      ? activeOrg
      : null;
  const patientCountQuery = useQuery({
    ...orpc.org.getWorkspacePatientCount.queryOptions({
      input: { organizationId: untypedWorkspace?.id ?? "" },
    }),
    enabled: !!untypedWorkspace && isOwner,
  });

  if (!isInviteAcceptPage && !isDeletePatientDataPage && actorContextQuery.isError) {
    return <PersonaGateError retry={() => { void actorContextQuery.refetch(); }} />;
  }

  if (isOrgMember && activeOrgQuery.isError) {
    return <PersonaGateError retry={() => { void activeOrgQuery.refetch(); }} />;
  }

  if (!untypedWorkspace) return <>{children}</>;

  // Owner-ness decides between the prompt and a pass-through, so wait for the
  // member row rather than flashing the dashboard at an owner.
  if (activeMemberQuery.isLoading || patientCountQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-body text-muted-foreground">
        Loading workspace...
      </div>
    );
  }

  if (activeMemberQuery.isError) {
    return <PersonaGateError retry={() => { void activeMemberQuery.refetch(); }} />;
  }

  if (!isOwner) return <>{children}</>;

  if (patientCountQuery.isError) {
    return <PersonaGateError retry={() => { void patientCountQuery.refetch(); }} />;
  }

  return (
    <OnboardingPageLayout>
      <PersonaWorkspaceForm
        existingWorkspace={{ id: untypedWorkspace.id, name: untypedWorkspace.name }}
        existingPatientCount={patientCountQuery.data?.patientCount ?? 0}
      />
    </OnboardingPageLayout>
  );
}
